import { Edit } from "lucide-react";
import { useDefaultGoals } from "@/app/hooks/useDefaultGoals";
import { formatMoneyValue } from "@/app/utils/formatMoneyValue";
import { getPartnerCardStyle } from "@/app/utils/partners";
import { PartnerType } from "@/app/utils/partners.enum";

interface SupervisorCommissionsProps {
  commissions?: Record<string, any>;
  onEdit: () => void;
}

export const SupervisorCommissions = ({
  commissions,
  onEdit,
}: SupervisorCommissionsProps) => {
  const { tables } = useDefaultGoals();
  const allTables =
    tables?.pages?.[0]?.tables?.filter((t: any) => t.isActive !== false) || [];

  const getTableName = (tableId: string) => {
    const table = allTables.find((t: any) => t._id === tableId);
    return table?.name || "Nome indisponível";
  };

  const formatCommissionValue = (value: number, isMoney: boolean) => {
    if (isMoney) {
      return formatMoneyValue(value);
    } else {
      return `${Number(value).toFixed(2).replace(".", ",")}%`;
    }
  };

  const formatRangeValue = (value: number, isMoney: boolean) => {
    if (isMoney) {
      return formatMoneyValue(value);
    }
    return `${value} parcelas`;
  };

  return (
    <div className="my-5 space-y-3">
      <div className="group flex items-center gap-2">
        <h2 className="font-semibold">Comissão supervisor</h2>
        <Edit
          onClick={onEdit}
          size={16}
          className="hidden cursor-pointer text-gray-500 hover:text-gray-700 group-hover:block"
        />
      </div>
      <div className="space-y-2">
        {(commissions || {}) &&
          Object.entries(commissions || {}).map(([partnerKey, config]) => {
            if (!config || !config.items || config.items.length === 0)
              return null;

            const isCommissionMoney = partnerKey === PartnerType.SALVCAR;
            const isRangeMoney =
              isCommissionMoney || partnerKey === PartnerType.PAN;
            const isFGTS = partnerKey === PartnerType.FGTS;
            const style = getPartnerCardStyle(partnerKey);

            return (
              <div
                key={partnerKey}
                className="rounded-lg border p-3"
                style={{
                  backgroundColor: style.bg,
                  color: style.text,
                  borderColor: style.border,
                }}
              >
                <p className="mb-2 text-xs font-bold uppercase tracking-wider opacity-90">
                  {partnerKey}
                </p>
                <div className="space-y-1.5">
                  {config.items.map((item: any, idx: number) => {
                    const commissionValue = item.supervisorCommission;
                    if (
                      commissionValue === undefined ||
                      commissionValue === null
                    )
                      return null;

                    let label = "";
                    if (isFGTS && item._id) {
                      label = getTableName(item._id);
                    } else if (item.value !== undefined) {
                      label = `A partir de ${formatRangeValue(item.value, isRangeMoney)}`;
                    }

                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="font-medium">
                          {label || `Item ${idx + 1}`}
                        </span>
                        <span
                          className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                          style={{ backgroundColor: "rgba(255,255,255,0.25)" }}
                        >
                          {formatCommissionValue(
                            commissionValue,
                            isCommissionMoney,
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};
